import { publicCatalogApi } from '@/features/publicsite/catalog/api/publicCatalogApi';
import { CatalogCard } from '@/features/publicsite/catalog/CatalogCard';
import { CatalogSection } from '@/features/publicsite/catalog/CatalogSection';
import { CatalogSubsection } from '@/features/publicsite/catalog/CatalogSubsection';
import type { Association } from '@/features/publicsite/types/community/Association';
import { forumPath } from '@/features/common/forumPath';
import type { Forum } from '@/features/common/types/Forum';
import type { QaSession } from '@/features/publicsite/types/community/QaSession';
import { usePublicList } from '@/features/publicsite/usePublicList';
import { publicCatalog } from '@/i18n/dictionaries/publicCatalog';
import { useTranslation } from '@/i18n/useTranslation';

export function CommunityPage() {
  const { t } = useTranslation(publicCatalog);
  const forums = usePublicList<Forum>(publicCatalogApi.forumsUrl);
  const associations = usePublicList<Association>(publicCatalogApi.associationsUrl);
  const qaSessions = usePublicList<QaSession>(publicCatalogApi.qaUrl);

  // The only one of the five with two lists under two headings, so the section shell carries the
  // page header alone (items={null} renders neither a grid nor an empty state) and both lists are
  // subsections. Trying to make one of them the section's own grid put the second heading in the
  // wrong place.
  return (
    <CatalogSection
      eyebrow={t('eyebrowCommunity')}
      heading={t('communityHeading')}
      lead={t('communityLead')}
      items={null}
      renderCard={() => null}
    >
      <CatalogSubsection
        heading={t('communityForums')}
        items={forums}
        renderCard={(f) => (
          // The public list carries no id column beyond the anchor, so the anchor doubles as
          // the fragment target every other catalogue card carries too.
          <CatalogCard key={f.id} id={`forum-${f.id}`} title={f.title} to={forumPath(f)}>
            <p>{f.description}</p>
          </CatalogCard>
        )}
      />
      <CatalogSubsection
        heading={t('communityQa')}
        items={qaSessions}
        renderCard={(s) => (
          // No status badge: nothing in the backend ever sets a session to CLOSED, so every
          // session that ever reaches this list already reads OPEN — the same reason Forumuri
          // and Asociații lost theirs.
          <CatalogCard key={s.id} title={s.title} to={`/comunitate/qa/${s.id}`} />
        )}
      />
      <CatalogSubsection
        heading={t('communityAssociations')}
        items={associations}
        renderCard={(a) => (
          <CatalogCard key={a.id} title={a.name} to={`/comunitate/asociatii/${a.id}`} />
        )}
      />
    </CatalogSection>
  );
}
